import React, { useEffect, useState } from 'react';
import './geography.scss';

import { useDispatch, useSelector, useStore } from 'react-redux';
import fileDownload from 'js-file-download';
import {
  getBoundsFromPoints,
  getBoundsFromPolygons,
  getColorByType,
  getPaginationOpts, getResponseError, getZIndexByType,
  isCoordinatePair,
  responseLogWithNotification,
  useFilterForm,
  useUpdatePeriod,
} from '../../../utils/utils';
import { getSystemData } from '../../System/reducer';
import { getGeographyData } from '../reducer';
import Loader from '../../../components/base/Loader/Loader';
import {
  deleteGeography, exportGeography, importGeography, loadGeographyByIdClear, loadGeographyList,
} from '../actions';
import { GEOGRAPHY_MODULE } from '../../../configs/moduleNames';
import YMap from '../../../components/base/Map/YMap';
import { getPolygon } from '../../../components/base/Map/mapUtils';
import GeographyList from './GeographyList';
import GeographyForm from './GeographyForm';
import { showGlobalConfirm } from '../../System/actions';
import { errorNotification, successNotification, warningNotification } from '../../Notifications/notificationDecorator';
import { loadTelemetryListAll } from '../../Telemetry/actions';
import { getTelemetryData } from '../../Telemetry/reducer';
import { EVACUATIONS_STATUS_NAMES, STATUS_COLOR_CODE } from '../../../configs';
import { dateTimeFormatter } from '../../../utils/formatters';

export default function GeographyPage() {
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(false);
  const [polygon, setPolygon] = useState(null);
  const [geographyPolygons, setGeographyPolygons] = useState([]);
  const [mapInstance, setMapInstance] = useState(null);
  const { updatePeriod } = useSelector(getSystemData);
  const store = useStore();
  const {
    geographyListMeta,
    geographyList,
    loadingGeographyList,
    loadingCurrentGeography,
    loadingCreateGeography,
    loadingEditGeographyById,
    loadingDeleteGeography,
    loadingExportGeography,
    loadingImportGeography,
  } = useSelector(getGeographyData);

  const {
    loadingTelemetryListAll,
    telemetryListAll,
  } = useSelector(getTelemetryData);

  const loadData = opts => {
    const paginationOpts = getPaginationOpts(store.getState()[GEOGRAPHY_MODULE].geographyListMeta, opts);
    const requestData = {
      ...paginationOpts,
      filter: getValues(),
    };
    const p1 = dispatch(loadGeographyList(requestData)).catch(responseLogWithNotification);
    const p2 = dispatch(loadTelemetryListAll(requestData)).catch(responseLogWithNotification);
    return Promise.all([p1, p2]);
  };

  const { getValues, control } = useFilterForm({}, loadData);
  const [setPeriod] = useUpdatePeriod(loadData, updatePeriod);
  useEffect(() => setPeriod(updatePeriod), [updatePeriod]);
  useEffect(() => {
    if (mapInstance) {
      if (selected && !geographyList.find(item => item.geography_id === selected)) {
        setSelected(null);
        setPolygon(null);
      }
      const p = [];
      geographyList.forEach(obj => {
        (obj?.geography_json?.features || []).forEach((f, i) => {
          const itemPolygon = getPolygonFromGeography(f, obj.geography_type, obj.name, `${obj.geography_id}_${i}`);
          if (itemPolygon) {
            p.push(itemPolygon);
          }
        });
      });
      setGeographyPolygons(p);
      setBound();
    }
  }, [geographyList, mapInstance, selected]);

  useEffect(() => {
    let clusterer = null;
    if (mapInstance) {
      clusterer = createClusterer(telemetryListAll);
      if (clusterer) {
        mapInstance.geoObjects.add(clusterer);
      }
    }
    return () => {
      if (mapInstance?.geoObjects && clusterer) {
        mapInstance.geoObjects.remove(clusterer);
      }
    };
  }, [telemetryListAll, mapInstance]);

  const createClusterer = list => {
    const validList = (list || []).filter(item => isCoordinatePair([item?.gps?.lat, item?.gps?.lng]));
    if (!validList.length) {
      return null;
    }
    const clusterer = new window.ymaps.Clusterer({
      // Макет метки кластера pieChart.
      clusterIconLayout: 'default#pieChart',
      // Радиус диаграммы в пикселях.
      clusterIconPieChartRadius: 25,
      // Радиус центральной части макета.
      clusterIconPieChartCoreRadius: 10,
      // Ширина линий-разделителей секторов и внешней обводки диаграммы.
      clusterIconPieChartStrokeWidth: 2,
      // // Определяет наличие поля balloon.
      // hasBalloon: false

      // preset: 'islands#invertedVioletClusterIcons',
      // groupByCoordinates: false,
      clusterDisableClickZoom: true,
      clusterHideIconOnBalloonOpen: false,
      geoObjectHideIconOnBalloonOpen: false,
    });
    const getPointData = index => ({
      balloonContentHeader: `<div>Устройство ${validList[index]?.id}</div>`,
      balloonContentBody: `Координаты ${validList[index]?.gps?.lng ? `${validList[index].gps.lat}, ${validList[index].gps.lng}` : ''}`,
      balloonContentFooter: dateTimeFormatter(validList[index].timestamp),
      clusterCaption: `Устройство <br> <strong>${validList[index]?.id}</strong>`,
    });

    const getPointOptions = index => {
      const { status } = validList[index];
      const colorName = EVACUATIONS_STATUS_NAMES[status];
      const color = STATUS_COLOR_CODE[colorName];
      return {
        preset: 'islands#icon',
        iconColor: color,
      };
    };
    const geoObjects = [];

    for (let i = 0, len = validList.length; i < len; i++) {
      const item = validList[i];
      geoObjects.push(new window.ymaps.Placemark([Number(item.gps.lat), Number(item.gps.lng)], getPointData(i), getPointOptions(i)));
    }
    clusterer.options.set({
      gridSize: 80,
      clusterDisableClickZoom: true,
      balloonContentLayoutWidth: 800,
      clusterBalloonLeftColumnWidth: 300,
    });
    clusterer.add(geoObjects);
    return clusterer;
  };

  const handleClose = () => {
    clearCurrentState();
    setShowForm(false);
    setPolygon(null);
    setSelected(null);
  };

  const handleCreate = () => {
    if (!mapInstance) {
      warningNotification('Карта еще не загружена', 'Попробуйте чуть позже');
      return;
    }
    clearCurrentState();
    setShowForm(true);
    setSelected(null);
    setPolygon(getPolygonFromGeography({}));
  };

  const handleSelect = (item, isEdit = false) => () => {
    if (selected === item.geography_id && !isEdit) {
      setSelected(null);
      setPolygon(null);
      setBound();
      return;
    }
    const p = [];
    if (selected !== item.geography_id) {
      setSelected(item.geography_id);
      (item?.geography_json?.features || []).forEach((f, i) => {
        const itemPolygon = getPolygonFromGeography(f, item.geography_type, item.name, `${item.geography_id}_${i}`);
        if (itemPolygon) {
          p.push(itemPolygon);
        }
      });
      setPolygon(p[0]); // todo такое решение что бы не переделывать на редактирование гео объекта
    }
    // setBound(item?.geography_json?.features?.[0]?.geometry?.coordinates?.[0]);
  };
  const setBound = coordinates => {
    if (mapInstance) {
      let bound = null;
      if (coordinates?.length) {
        bound = getBoundsFromPoints(coordinates);
      } else {
        bound = getBoundsFromPolygons(
          geographyList.flatMap(g => (g?.geography_json?.features || []).map(feature => feature?.geometry?.coordinates)),
        );
      }
      if (bound) {
        mapInstance.setBounds(bound, { checkZoomRange: true });
      }
    }
  };

  const handleEdit = item => event => {
    if (!mapInstance) {
      warningNotification('Карта еще не загружена', 'Попробуйте чуть позже');
      return;
    }
    handleSelect(item, true)();
    setShowForm(true);
    event.stopPropagation();
    event.preventDefault();
  };

  const handleDelete = item => event => {
    dispatch(showGlobalConfirm({
      confirmMessage: `Удаление зоны "${item.name || 'Без имени'}"`,
      confirmButtonText: 'Удалить',
      confirmClassName: 'remove-preset',
    })).then(() => {
      dispatch(deleteGeography(item.geography_id)).then(() => {
        if (selected === item.geography_id) {
          setSelected(null);
          setPolygon(null);
        }
        loadData();
        successNotification('Удаление зоны', 'Удаление прошло успешно');
      }).catch(response => {
        errorNotification('Удаление зоны', getResponseError(response));
      });
    }).catch(() => {});

    event.preventDefault();
    event.stopPropagation();
    return false;
  };

  const handleImport = files => {
    if (!files) {
      return;
    }
    const data = new FormData();
    for (let i = 0, len = files.length; i < len; i++) {
      const file = files[i];
      data.append('file', file);
    }
    dispatch(importGeography(data))
      .then(() => {
        successNotification('Импорт зон', 'Процесс импорта прошел успешно');
        loadData();
      })
      .catch(response => errorNotification('Импорт зон', getResponseError(response)));
    handleClose();
  };

  const handleExport = () => {
    dispatch(exportGeography())
      .then(data => {
        if (data) { fileDownload(data, data.filename); }
      })
      .catch(response => errorNotification('Экспорт зон', getResponseError(response)));
    // handleClose();
  };

  const clearCurrentState = () => {
    dispatch(loadGeographyByIdClear()).catch(responseLogWithNotification);
  };

  const getPolygonFromGeography = (feature, geography_type = '', name = '', id = '') => {
    const p = {
      coordinates: feature?.geometry?.coordinates || [],
      property: {
        id,
        name,
        color: getColorByType(geography_type),
        zIndex: getZIndexByType(geography_type),
      },
    };
    return getPolygon(p);
  };
  const selectedElement = geographyList.find(item => item.geography_id === selected);
  return (
    <div className="page application-page">
      <Loader loading={loadingTelemetryListAll || loadingGeographyList || loadingCurrentGeography || loadingDeleteGeography || loadingCreateGeography || loadingEditGeographyById || loadingExportGeography || loadingImportGeography} />
      <div className="page-content-wrapper">
        <div className="geography-columns">
          {!showForm ? (
            <GeographyList
              handleCreate={handleCreate}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              handleSelect={handleSelect}
              handleImport={handleImport}
              handleExport={handleExport}
              loadData={loadData}
              selected={selected}
              geographyListMeta={geographyListMeta}
              geographyList={geographyList}
              control={control}
            />
          ) : false}
          {showForm
            ? (
              <GeographyForm
                handleClose={handleClose}
                loadData={loadData}
                geography={selectedElement}
                polygon={polygon}
                mapInstance={mapInstance}
              />
            )
            : false}
          <div className="map-columns card">
            <YMap
              classNames="geography-map"
              polygons={polygon ? [polygon] : geographyPolygons}
              setMapInstance={i => setMapInstance(i)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
